<template>
  <div class="my-orders">
    <el-card>
      <template #header>
        <div class="card-header">
          <h2>我的课程订单</h2>
        </div>
      </template>

      <!-- 订单列表 -->
      <el-table
        v-loading="loading"
        :data="orders"
        style="width: 100%"
      >
        <el-table-column prop="order_no" label="订单号" width="180" />
        
        <el-table-column label="课程信息" min-width="310">
          <template #default="{ row }">
            <div class="course-info">
              <el-image
                :src="row.course_cover"
                fit="cover"
                class="course-cover"
              >
                <template #error>
                  <div class="image-slot">
                    <el-icon><Picture /></el-icon>
                  </div>
                </template>
              </el-image>
              <div class="course-details">
                <div class="course-title">{{ row.course_title }}</div>
                <div class="course-meta">
                  <span>{{ row.course_duration }}课时</span>
                  <span>讲师：{{ row.teacher }}</span>
                </div>
              </div>
            </div>
          </template>
        </el-table-column>
        
        <el-table-column prop="amount" label="金额" width="120">
          <template #default="{ row }">
            <span class="amount">¥{{ row.amount }}</span>
          </template>
        </el-table-column>
        
        <el-table-column prop="status" label="订单状态" width="120">
          <template #default="{ row }">
            <el-tag :type="getStatusType(row.status)">
              {{ getStatusText(row.status) }}
            </el-tag>
          </template>
        </el-table-column>
        
        <el-table-column prop="course_status" label="课程状态" width="120">
          <template #default="{ row }">
            <el-tag :type="row.course_status === 1 ? 'success' : 'info'">
              {{ row.course_status === 1 ? '已上课' : '未上课' }}
            </el-tag>
          </template>
        </el-table-column>
        
        <el-table-column prop="create_time" label="下单时间" width="180" />
        
        <el-table-column label="操作" width="200" fixed="right">
          <template #default="{ row }">
            <el-button
              v-if="row.status === 0"
              type="primary"
              size="small"
              @click="handlePay(row)"
            >
              去支付
            </el-button>
            <el-button
              size="small"
              @click="handleViewDetail(row)"
            >
              查看详情
            </el-button>
          </template>
        </el-table-column>
      </el-table>

      <!-- 分页 -->
      <div class="pagination-wrapper" v-if="total > 0">
        <el-pagination
          v-model:current-page="pagination.page"
          v-model:page-size="pagination.pageSize"
          :total="total"
          :page-sizes="[10, 20, 30, 50]"
          layout="total, sizes, prev, pager, next, jumper"
          @size-change="handleSizeChange"
          @current-change="handlePageChange"
        />
      </div>

      <!-- 空状态 -->
      <el-empty v-if="!loading && orders.length === 0" description="暂无订单" />
    </el-card>

    <!-- 支付弹窗 -->
    <PaymentModal
      v-model="paymentVisible"
      :order-info="currentOrder"
      @confirm="handlePaymentConfirm"
      @close="handlePaymentClose"
    />
  </div>
</template>

<script>
import courseApi from '@/api/course'
import PaymentModal from '@/components/PaymentModal.vue'
import { Picture } from '@element-plus/icons-vue'
import { ORDER_TYPES, PAYMENT_METHODS } from '@/utils/constants'

export default {
  name: 'MyOrders',
  
  components: {
    PaymentModal,
    Picture
  },
  
  data() {
    return {
      orders: [],
      total: 0,
      pagination: {
        page: 1,
        pageSize: 10
      },
      loading: false,
      paymentVisible: false,
      currentOrder: {
        type: ORDER_TYPES.COURSE,
        name: '',
        amount: 0,
        orderId: null
      }
    }
  },
  
  mounted() {
    this.loadOrders()
  },
  
  methods: {
    /**
     * 加载订单列表
     */
    async loadOrders() {
      this.loading = true
      
      try {
        const res = await courseApi.getMyOrders({
          page: this.pagination.page,
          page_size: this.pagination.pageSize
        })
        
        if (res && res.code === 2000) {
          // 映射API返回的数据结构
          this.orders = (res.data.data || []).map(order => ({
            id: order.id,
            order_no: order.order_no,
            course_id: order.course_id,
            course_title: order.course?.course_name || '',
            course_cover: order.course?.course_imgs || '',
            course_duration: order.course?.class_hour || 0,
            teacher: order.member?.name || '',
            amount: order.pay_amount,
            status: order.pay_time ? 1 : 0, // 有支付时间表示已支付
            course_status: order.course_status,
            create_time: order.create_time,
            pay_time: order.pay_time,
            write_off_time: order.write_off_time
          }))
          this.total = res.data.total || 0
        }
      } catch (error) {
        console.error('加载订单失败:', error)
      } finally {
        this.loading = false
      }
    },
    
    /**
     * 获取状态类型
     */
    getStatusType(status) {
      const types = {
        0: 'warning', // 待支付
        1: 'success', // 已支付
        2: 'info'     // 已取消
      }
      return types[status] || 'info'
    },
    
    /**
     * 获取状态文本
     */
    getStatusText(status) {
      const texts = {
        0: '待支付',
        1: '已支付',
        2: '已取消'
      }
      return texts[status] || '未知'
    },
    
    /**
     * 去支付
     */
    handlePay(order) {
      this.currentOrder = {
        type: ORDER_TYPES.COURSE,
        name: order.course_title,
        amount: order.amount,
        orderId: order.id
      }
      this.paymentVisible = true
    },
    
    /**
     * 处理支付确认
     */
    async handlePaymentConfirm({ paymentMethod, orderInfo }) {
      try {
        const res = await courseApi.payOrder({
          order_id: orderInfo.orderId,
          payment_method: paymentMethod
        })
        
        if (res && res.code === 2000) {
          this.paymentVisible = false
          
          if (paymentMethod === PAYMENT_METHODS.BANK) {
            this.$message.success('支付信息已提交，等待审核')
          } else {
            this.$message.success('支付成功')
          }
          
          // 刷新订单列表
          this.loadOrders()
        }
      } catch (error) {
        console.error('支付失败:', error)
      }
    },
    
    /**
     * 关闭支付弹窗
     */
    handlePaymentClose() {
      this.paymentVisible = false
    },
    
    /**
     * 开始学习
     */
    handleStartCourse(order) {
      // 跳转到课程学习页面
      this.$router.push(`/course/learn/${order.course_id}`)
    },
    
    /**
     * 查看详情
     */
    handleViewDetail(order) {
      this.$router.push(`/course/detail/${order.course_id}`)
    },
    
    /**
     * 页码变化
     */
    handlePageChange(page) {
      this.pagination.page = page
      this.loadOrders()
    },
    
    /**
     * 每页数量变化
     */
    handleSizeChange(size) {
      this.pagination.pageSize = size
      this.pagination.page = 1
      this.loadOrders()
    }
  }
}
</script>

<style lang="scss" scoped>
.my-orders {
  min-height: 100vh;
  padding: 20px;
  background-color: #f5f7fa;
  
  .card-header {
    h2 {
      margin: 0;
      font-size: 20px;
      color: #303133;
    }
  }
  
  .course-info {
    display: flex;
    gap: 15px;
    
    .course-cover {
      width: 80px;
      height: 60px;
      border-radius: 4px;
      flex-shrink: 0;
      
      .image-slot {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 100%;
        height: 100%;
        background-color: #f5f7fa;
        color: #909399;
      }
    }
    
    .course-details {
      flex: 1;
      
      .course-title {
        font-size: 14px;
        color: #303133;
        margin-bottom: 5px;
        font-weight: 500;
      }
      
      .course-meta {
        font-size: 12px;
        color: #909399;
        
        span {
          margin-right: 15px;
        }
      }
    }
  }
  
  .amount {
    font-size: 16px;
    font-weight: bold;
    color: #f56c6c;
  }
  
  .pagination-wrapper {
    display: flex;
    justify-content: center;
    margin-top: 20px;
  }
}

@media (max-width: 768px) {
  .my-orders {
    padding: 10px;
    
    :deep(.el-table) {
      font-size: 12px;
    }
  }
}
</style>
